/**
 * Thin typed wrapper over the VaultPod viewer-api endpoints.
 * Keep the surface area small — just enough for QIDO / WADO-RS-metadata
 * calls. Pixel-data fetch goes through the worker.
 */

import { auth } from "../auth/store";

const BASE = "/viewer-api";

function authHeaders(): Record<string, string> {
  const s = auth();
  return s ? { Authorization: `Bearer ${s.accessToken}` } : {};
}

export type DicomwebStudy = {
  "0020000D"?: { vr: "UI"; Value: string[] }; // Study Instance UID
  "00100010"?: { vr: "PN"; Value: Array<{ Alphabetic: string }> }; // Patient Name
  "00100020"?: { vr: "LO"; Value: string[] }; // Patient ID
  "00080020"?: { vr: "DA"; Value: string[] }; // Study Date
  "00080050"?: { vr: "SH"; Value: string[] }; // Accession
  "00080060"?: { vr: "CS"; Value: string[] }; // Modality
  "00081030"?: { vr: "LO"; Value: string[] }; // Study Description
  "00201208"?: { vr: "IS"; Value: number[] }; // Number of Study Related Instances
};

export type Study = {
  studyUid: string;
  patientName: string;
  patientId: string;
  studyDate: string;
  accession: string;
  modality: string;
  description: string;
  instanceCount: number;
  // Optional workflow fields — not all DICOM PACS populate these
  // but the radiologist worklist uses them when present.
  priority?: "STAT" | "ROUTINE" | string;
  reportSigned?: boolean;
  assignedTo?: string;
};

function flatten(ds: DicomwebStudy): Study {
  return {
    studyUid:       ds["0020000D"]?.Value?.[0] ?? "",
    patientName:    ds["00100010"]?.Value?.[0]?.Alphabetic ?? "",
    patientId:      ds["00100020"]?.Value?.[0] ?? "",
    studyDate:      ds["00080020"]?.Value?.[0] ?? "",
    accession:      ds["00080050"]?.Value?.[0] ?? "",
    modality:       ds["00080060"]?.Value?.[0] ?? "",
    description:    ds["00081030"]?.Value?.[0] ?? "",
    instanceCount:  ds["00201208"]?.Value?.[0] ?? 0,
  };
}

export async function listStudies(query: Record<string, string> = {}): Promise<Study[]> {
  const params = new URLSearchParams(query);
  const url = `${BASE}/qido/studies${params.toString() ? "?" + params : ""}`;
  const rsp = await fetch(url, { headers: { Accept: "application/dicom+json", ...authHeaders() } });
  if (!rsp.ok) throw new Error(`QIDO studies: ${rsp.status}`);
  const rows: DicomwebStudy[] = await rsp.json();
  return rows.map(flatten);
}

/** QIDO-RS single-study summary — patient name/id/date/modality for
 *  banner and chrome. Returns undefined if the study isn't visible to
 *  the current user.
 *
 *  Guard: the Zig QIDO backend currently ignores
 *  `?StudyInstanceUID=` and returns the full study index, so we
 *  filter client-side after the round-trip. Without this the banner
 *  shows whichever study happens to come first in the list (usually
 *  a legacy CT). Tracked as a backend fix; drop the `.find()` once
 *  dicomweb-pg honours the filter. */
export async function getStudySummary(studyUid: string): Promise<Study | undefined> {
  const rows = await listStudies({ StudyInstanceUID: studyUid });
  return rows.find((r) => r.studyUid === studyUid) ?? rows[0];
}

export async function getStudyMetadata(studyUid: string): Promise<DicomwebStudy> {
  const rsp = await fetch(`${BASE}/wado/studies/${studyUid}/metadata`, {
    headers: { Accept: "application/dicom+json", ...authHeaders() },
  });
  if (!rsp.ok) throw new Error(`WADO metadata: ${rsp.status}`);
  const rows: DicomwebStudy[] = await rsp.json();
  return rows[0] ?? {};
}

export async function fetchFrameBytes(
  studyUid: string,
  seriesUid: string,
  instanceUid: string,
  frameNumber: number = 1,
): Promise<ArrayBuffer> {
  const url = `${BASE}/wado/studies/${studyUid}/series/${seriesUid}/instances/${instanceUid}/frames/${frameNumber}`;
  const rsp = await fetch(url, {
    headers: { Accept: "multipart/related; type=\"application/octet-stream\"", ...authHeaders() },
  });
  if (!rsp.ok) throw new Error(`WADO frames: ${rsp.status}`);
  return await rsp.arrayBuffer();
}

export interface StudyInstance {
  studyUid: string;
  seriesUid: string;
  seriesNumber: number;
  seriesDescription: string;
  modality: string;
  instanceUid: string;
  instanceNumber: number;
  /** SOP Class UID — `00080016`. Used by the endoscopy shell to
   *  route VL Endoscopic Image Storage instances to the stills
   *  viewer and Video Endoscopic Image Storage to the video player.
   *  Empty string when the QIDO row didn't carry the tag (legacy
   *  imports). */
  sopClassUid: string;
}

type DicomwebSeries = {
  "0020000E"?: { vr: "UI"; Value: string[] };  // Series UID
  "00200011"?: { vr: "IS"; Value: number[] };  // Series Number
  "0008103E"?: { vr: "LO"; Value: string[] };  // Series Description
  "00080060"?: { vr: "CS"; Value: string[] };  // Modality
};
type DicomwebInstance = {
  "00080018"?: { vr: "UI"; Value: string[] };  // SOP Instance UID
  "00080016"?: { vr: "UI"; Value: string[] };  // SOP Class UID
  "00200013"?: { vr: "IS"; Value: number[] };  // Instance Number
};

/**
 * Enumerate every instance in a study, grouped by series. Used by the
 * viewer to build the thumbnail + scroll list.
 */
export async function listStudyInstances(studyUid: string): Promise<StudyInstance[]> {
  console.info("[vpd/listStudyInstances] fetching series for", studyUid);
  const seriesRsp = await fetch(`${BASE}/qido/studies/${studyUid}/series`, {
    headers: { Accept: "application/dicom+json", ...authHeaders() },
  });
  console.info("[vpd/listStudyInstances] series status", seriesRsp.status);
  if (!seriesRsp.ok) throw new Error(`QIDO series: ${seriesRsp.status}`);
  const seriesRows: DicomwebSeries[] = await seriesRsp.json();
  console.info("[vpd/listStudyInstances] series rows", seriesRows.length);

  const out: StudyInstance[] = [];
  for (const s of seriesRows) {
    const seriesUid = s["0020000E"]?.Value?.[0] ?? "";
    if (!seriesUid) continue;
    const instRsp = await fetch(
      `${BASE}/qido/studies/${studyUid}/series/${seriesUid}/instances`,
      { headers: { Accept: "application/dicom+json", ...authHeaders() } },
    );
    console.info("[vpd/listStudyInstances] instances status", instRsp.status, "for series", seriesUid);
    if (!instRsp.ok) continue;
    const rows: DicomwebInstance[] = await instRsp.json();
    console.info("[vpd/listStudyInstances] instance rows", rows.length);
    for (const r of rows) {
      out.push({
        studyUid,
        seriesUid,
        seriesNumber:      s["00200011"]?.Value?.[0] ?? 0,
        seriesDescription: s["0008103E"]?.Value?.[0] ?? "",
        modality:          s["00080060"]?.Value?.[0] ?? "",
        instanceUid:       r["00080018"]?.Value?.[0] ?? "",
        instanceNumber:    r["00200013"]?.Value?.[0] ?? 0,
        sopClassUid:       r["00080016"]?.Value?.[0] ?? "",
      });
    }
  }
  out.sort((a, b) =>
    a.seriesNumber - b.seriesNumber ||
    a.instanceNumber - b.instanceNumber,
  );
  console.info("[vpd/listStudyInstances] returning", out.length, "instances");
  return out;
}

/**
 * WADO-RS single-instance retrieve as application/dicom. Returns the
 * raw Part-10 bytes the scp-daemon stored to S3.
 */
export async function fetchInstanceBytes(
  studyUid: string,
  seriesUid: string,
  instanceUid: string,
): Promise<ArrayBuffer> {
  const url = `${BASE}/wado/studies/${studyUid}/series/${seriesUid}/instances/${instanceUid}`;
  const rsp = await fetch(url, { headers: { Accept: "application/dicom", ...authHeaders() } });
  if (!rsp.ok) throw new Error(`WADO instance: ${rsp.status}`);
  return await rsp.arrayBuffer();
}
